import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import validator from 'validator';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import config from '../../config/Constant';
import { postRequestWithoutAccessToken, getRequest } from '../../helpers/apiHandler';
import CustomAlert from '../../components/CustomAlert';
import { setLoginSuccess } from '../../store/actions/reduxAction';
import LeftSection from './LeftSection';
import LoginWithGoogle from './LoginWithGoogle';
import { LinkedIn } from 'react-linkedin-login-oauth2';
import IconLinkedIn from '../../assets/linkedin-icon.png';

const baseUrl = process.env.PUBLIC_URL;
const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { access_token } = useSelector((state) => state.auth.user);

  const getRememberMe = sessionStorage.getItem('rememberMe');
  const parsedRememberMe = getRememberMe ? JSON.parse(getRememberMe) : { email: '', password: '' };

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(parsedRememberMe.email);
  const [password, setPassword] = useState(parsedRememberMe.password);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState({ error: false, message: '' });
  const [passwordError, setPasswordError] = useState({ error: false, message: '' });
  const [alertState, setAlertState] = useState({ show: false, variant: 'danger', message: '' });

  useEffect(() => {
    check_if_logged_in();
  }, [access_token]);

  const check_if_logged_in = () => {
    if (!access_token) return navigate('/login');

    const data = { headers: { Authorization: access_token.token } };
    getRequest('/user/me', data, (res) => {
      if (res.success) {
        const { active, phone_active, role, is_deleted } = res.data;

        if (is_deleted) return navigate('/account-suspended');
        if (role === "admin") return navigate('/admin/dashboard');
        if (role === "manager") return navigate('/manager/dashboard');
        if (!active) return navigate(`/email/verify/${access_token.token}`);
        if (!phone_active) return navigate(`/phone/verify/${access_token?.token}`);
        if (phone_active && active) return navigate('/dashboard');
      }
    });
  }

  const performPostRequest = (data) => {
    setLoading(true);
    postRequestWithoutAccessToken('/auth/login', data, (res) => {
      if (res.success) {
        dispatch(setLoginSuccess(res));
        const { phone_active, active, access_token, role, is_deleted } = res.data;

        if (is_deleted) return navigate('/account-suspended');
        if (role === "admin") return navigate('/admin/dashboard');
        if (role === "manager") return navigate('/manager/dashboard');
        if (active && phone_active) return navigate('/dashboard');
        if (!active) return navigate(`/email/verify/${access_token?.token}`);
        if (!phone_active && active) return navigate(`/phone/verify/${access_token?.token}`);
      }
      if (res.error) {
        if (res.message.includes('password parameter')) {
          setPasswordError({ error: true, message: 'The password you entered is incorrect.' });
        } else {
          setAlertState({ show: true, message: res.message, variant: 'danger' });
        }
      }
      setLoading(false);
    });
  };

  const handleErrors = () => {
    if (validator.isEmpty(email)) {
      setEmailError({ error: true, message: 'Please enter your email address.' });
      return false;
    }
    if (!validator.isEmail(email)) {
      setEmailError({ error: true, message: 'Please enter valid email address.' });
      return false;
    }
    if (validator.isEmpty(password)) {
      setPasswordError({ error: true, message: 'Please enter your password.' });
      return false;
    }
    return true;
  };

  const handleLoginSubmit = (evt) => {
    evt.preventDefault();
    if (!handleErrors()) return;

    if (rememberMe) {
      sessionStorage.setItem('rememberMe', JSON.stringify({ email, password }));
    }

    const data = { email, password, member: true };
    performPostRequest(data);
  };

  const handleLinkedInAuth = (code) => {
    if (code) {
      setAlertState({
        show: true,
        variant: 'success',
        message: 'You have successfully logged in with your LinkedIn account!',
      });

      const data = {
        social_platform: config.social_login_platforms.linkedin,
        token: code,
      };
      postRequestWithoutAccessToken('/auth/social-login', data, (res) => {
        if (res.success) {
          dispatch(setLoginSuccess(res));
          const { phone_active, active, access_token } = res.data;
          localStorage.setItem("token", access_token?.token);

          if (active && phone_active) return navigate('/dashboard');
          if (!active) return navigate(`/email/verify/${access_token?.token}`);
          if (!phone_active && active) return navigate(`/phone/verify/${access_token?.token}`);
        }
        if (res.error) {
          setAlertState({ show: true, message: res.message, variant: 'danger' });
        }
      });
    } else {
      setAlertState({ show: true, variant: 'danger', message: 'Linkedin error try again' });
    }
  };

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({ message: '', show: false, variant: '' });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <div className="Authenticate Login">
      <div className="container-auth-grid">
        <LeftSection />
        <div className="right-container">
          <div className="form-container-outter">
            <div className="form-container">
              <div className="form-top-section">
                <h3>Sign In</h3>
                {!white_label_perkeso && (
                  <div>
                    Don't have an account? <Link to={`${baseUrl}/signup`}>Sign Up</Link>
                  </div>
                )}
              </div>
              <Form noValidate onSubmit={handleLoginSubmit}>
                <CustomAlert
                  message={alertState.message}
                  show={alertState.show}
                  variant={alertState.variant}
                  closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
                />
                <Form.Group style={{ marginBottom: 20 }}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    required
                    type="email"
                    placeholder="Your email"
                    name="email"
                    value={email}
                    onChange={(evt) => {
                      setEmail(evt.target.value);
                      setEmailError({ error: false, message: '' });
                    }}
                    isInvalid={emailError.error}
                  />
                  <Form.Control.Feedback type="invalid">{emailError.message}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group style={{ marginBottom: 30 }}>
                  <Form.Label>Password</Form.Label>
                  <InputGroup className="input-group-password" hasValidation>
                    <Form.Control
                      required
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Your Password"
                      name="password"
                      value={password}
                      onChange={(evt) => {
                        setPassword(evt.target.value);
                        setPasswordError({ error: false, message: '' });
                      }}
                      isInvalid={passwordError.error}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text onClick={() => setShowPassword(!showPassword)}>
                        <img
                          src={require(`../../assets/AlertIcons/${showPassword ? 'icon-eye.svg' : 'icon-eye-close.svg'}`)}
                          width={24}
                          height={24}
                          alt="..."
                        />
                      </InputGroup.Text>
                    </InputGroup.Append>
                    <Form.Control.Feedback type="invalid">
                      {passwordError.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="d-flex d-sm-none justify-content-between">
                  <Form.Check
                    custom
                    id="remember-me"
                    name="rememberMe"
                    checked={rememberMe}
                    type="checkbox"
                    label="Remember Me"
                    onChange={(evt) => setRememberMe(evt.target.checked)}
                  />
                  <Link to="/request-forgot-password">Forgot Password?</Link>
                </Form.Group>
                <Form.Group style={{ marginBottom: 0 }}>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    style={{ minWidth: '160px' }}
                  >
                    {loading && <Spinner animation="border" size="sm" className="mr-2" />}
                    Sign In
                  </Button>
                </Form.Group>
              </Form>
              <div className="form-bottom-section d-none d-sm-flex">
                <Form.Check
                  custom
                  id="remember-me"
                  name="rememberMe"
                  checked={rememberMe}
                  type="checkbox"
                  label="Remember Me"
                  onChange={(evt) => setRememberMe(evt.target.checked)}
                />
                <Link to="/request-forgot-password">Forgot Password?</Link>
              </div>
              {!white_label_perkeso && (
                <>
                  <div className="divider-with-text" style={{ margin: '40px 0 20px' }}>
                    <span>Or Sign in with</span>
                  </div>
                  <div className="form-other-login">
                    <div className="social-login">
                      <LoginWithGoogle />
                      <LinkedIn
                        clientId={config.social_login_appId.linkedin}
                        scope="openid,profile,email"
                        state=""
                        redirectUri={`${window.location.origin}/linkedin`}
                        onSuccess={handleLinkedInAuth}
                      >
                        {({ linkedInLogin }) => (
                          <button
                            className="btn btn-social linkedin-btn"
                            type="button"
                            onClick={linkedInLogin}
                          >
                            <img src={IconLinkedIn} alt="..." />
                            <span>Sign in with Linkedin</span>
                          </button>
                        )}
                      </LinkedIn>
                    </div>
                  </div>
                </>
              )}
              <div className="copyright-section outline-text">
                @{new Date().getFullYear()} All rights reserved by elfo
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;